import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

const LoadingContext = createContext({
  isLoading: false
});

export const LoadingProvider = ({ children }) => {
  const [activeRequests, setActiveRequests] = useState(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
      return undefined;
    }

    const originalFetch = window.fetch.bind(window);

    const wrappedFetch = async (...args) => {
      setActiveRequests((prev) => prev + 1);
      try {
        const response = await originalFetch(...args);
        return response;
      } finally {
        if (isMountedRef.current) {
          setActiveRequests((prev) => Math.max(prev - 1, 0));
        }
      }
    };

    window.fetch = wrappedFetch;

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const value = useMemo(() => ({
    isLoading: activeRequests > 0
  }), [activeRequests]);

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {activeRequests > 0 && (
        <div className="global-loading-overlay" aria-live="polite" aria-busy="true">
          <div className="loading-indicator">
            <span className="loading-spinner" />
            <span className="loading-text">Loading…</span>
          </div>
        </div>
      )}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => useContext(LoadingContext);

